/**
 * The copy register (mission 6 T014; FR-025, C-007, SC-010; spec "Copy
 * register"; research §1.11; post-tasks P1, P2, R11; post-analysis U1).
 *
 * Every customer-facing sentence mission 6 renders that the PRD, the style
 * guide or a contract does not give lives here, once, with its owner and
 * status, for the copy owner to review. None is a compliance surface (those are
 * `mandatory-copy.ts`), and none renders a provisional tag: `status` is data.
 * An entry the owner accepted is `approved` and names the decision
 * (`OWNER_DECISION`, the mission 6 decision sheet, owner decision 2026-09-26);
 * every other entry stays `provisional`.
 * Each entry is a `text`, or a `render` that returns a string; a sentence that
 * carries a link exposes the link label as its own entry.
 *
 * Numeric arguments take a number, formatted here with `format.count` or
 * `format.bytes`, or a string the caller already formatted. Date arguments are
 * strings the caller formats with `format.date`.
 *
 * Every sentence is sentence case, second person, says "credit data reporter"
 * where a role is named, and stays under the T.3 ceiling (CLAUDE.md §3.6, §3.7;
 * FR-024). `copy-ceiling.test.ts` checks each entry; a claim pattern is allowed
 * only on an entry that carries `ceilingReviewed`, naming the decision or the
 * reviewer that read it (R11).
 *
 * Frozen after mission 6 WP02 (T17). Wording changes go in the handoff note for WP13.
 *
 * Mission 7 WP07 (T042; FR-024, C-009, SC-011; research §1.21; contracts/ui-api.md
 * "Register keys"; squad C11, U8, U12, U27, P3, P4; analysis U5; OR-11) adds
 * registers 19 to 31, the spec's copy register items 1 to 13, and F2's
 * `fact.firedRules`: 150 keys, planned up front so the screen lanes only use them.
 * Registers 32 and 33 (the root error page and the breadcrumbs) reuse registers
 * 16 and 17. Every new entry is provisional; the copy owner reads them.
 * - `rulingBy` names an owner outside copy who must rule on the sentence (five
 *   keys; OR-11, squad P4).
 * - `ceilingReviewed: 'pending: copy owner'` marks a claim-adjacent sentence
 *   nobody has read against the T.3 ceiling yet. Mission 7 self-certifies nothing.
 * - Naming constraint (not a ruling owner): `detail.*`, `testUnavailable` and
 *   `progressQueued` never name MGTS; they say "the delivery service".
 * - Bureau arguments are `Bureau` values, named through `bureauName`; result
 *   arguments are `TransmissionResult` values, named through the `resultLabel.*`
 *   words. Counts render the singular at 1 (squad U8).
 * Frozen after WP07; a screen that needs a key asks for a spine commit on lane-g.
 *
 * Mission 8 WP07 (T036; FR-014, FR-018, C-009; research §1.24; contracts/ui-api.md
 * "Register keys"; squad U7, U8, U9, U10, S7; TR-12, R4, R16; analysis A1) adds
 * register 34 (the assistant) and register 35 (the carried screen fixes), every
 * key up front, so the screen lanes only use them. The owner approved every
 * register 34 and 35 entry as written, the copy owner agreeing
 * (`OWNER_DECISION_M08`, 2026-09-28).
 * - `ceilingReviewed` sits only on the two claim-adjacent keys,
 *   `assistantPiiStatement` and `assistantThrottled`, each with a note naming the
 *   claim, and names the copy owner's reading (2026-09-28); the claim patterns run
 *   on every other key (TR-12).
 * - The assistant is "the assistant": never the provider's name, "AI",
 *   "chatbot" or "copilot" (U9). Severities are the words `ERROR` and `WARN`,
 *   never pluralised, and counts render the singular at 1 (U7, U8).
 * - `assistantPromptTriage` renders nothing at 0, so the composer hides it.
 * Frozen after WP07; a screen that needs a key asks for a spine commit on lane-g.
 *
 * Mission 9 WP11 (T068; FR-019, C-009, C-010; contracts/ui-api.md "Register keys"; squad S18, U25,
 * U29; TR-10) adds register 36, billing, every key up front, so the screen lanes (WP12 to WP14) only
 * use them. Every entry is owner copy and provisional, for the copy owner.
 * - Every figure and unit is an argument (U25): money is a `money()` string, a date a `format.date`
 *   string, a count a number; no price or limit is written into a sentence (C-010).
 * - `ceilingReviewed` sits only on `meteringStatement`, `planTermsTransmission` and
 *   `cardProviderNote` (S18), all unread; cardProviderNote's readers are the copy owner and
 *   Compliance. The claim patterns run on every other key.
 * - The read-only sentences say "your runs and files still in retention", never "everything" (U29).
 * - The fourteen `email*` keys (TR-10) are the provider's templates (mission 12): text holding the
 *   template's data keys as `{key}` placeholders (`email-data-keys.json`); the SPA never renders them.
 * Frozen after WP11; a screen that needs a key asks for a spine commit on lane-k.
 *
 * Mission 10 WP15 (T086; FR-022, C-007, C-008, C-013; spec "Copy register"; research §1.27 "Register 37";
 * squad S13, U17, U20, U26) adds register 37, retention and deletion, every key up front, so the screen lanes
 * (WP16 to WP18) only use them. Every entry is owner copy and provisional, for the copy owner.
 * - Every period is an argument the screen fills from the window read's `terms` (`{days}`, `{years}`,
 *   `{hours}`, `{minutes}`; FR-022, C-013): no period is written into a sentence. Dates are `format.date`
 *   strings and counts numbers.
 * - `legal` marks the sentences Legal must approve before GA: Q14 on the period and contract sentences,
 *   Q15 on the Class 2 sentences (spec Open Questions). `ownerGate` names the decision that gates the
 *   encryption statement, which names stored files only (spec amendment 3; DM-01M3VYK5GMZDXY4RGS9MHN0PS7).
 * - No sentence claims backups or crypto-shred, says "archived", "hidden" or "soft-deleted", or says
 *   retention 0 deletes immediately (C-008; Contradictions 1 and 6): `copy-ceiling.test.ts` walks register
 *   37 key by key. `auditType.backup_codes_regenerated` names the sign-in backup codes, which the C-008
 *   pattern also matches, so it is marked for the copy owner's reading.
 * - The gated labels (`retentionZeroSaveGated`, `retentionZeroDownloadGated`) are the demoted buttons'
 *   labels (style guide §7.1, §11; OR-15, U17). The four `email*` keys are the provider's templates over
 *   `email-data-keys.json`, which the SPA never renders.
 * Frozen after WP15; a screen that needs a key asks for a spine commit on lane-o.
 *
 * Counsel's memo of 2026-10-03 (Legal Q14, Q15; provisional pending partner review; docs/legal/README.md)
 * replaces the register 36 billing statements (B6) and the register 37 retention, export, deletion and email
 * sentences (C2 to C7). Each such entry carries `counsel` (the ask and `COUNSEL_MEMO`) in place of its `legal`
 * mark, stays `provisional`, and is counsel's text verbatim except for the conforming edits recorded in
 * docs/legal/README.md: periods render from `terms` (C-013), "AI assistant" is "assistant" (U9), C3's
 * "immediately" is "earlier" (Contradictions 6), C6's "immediately" is "now", and C6 names the storage
 * encryption key (AV.6 as amended).
 */
import { bureauName, type Bureau } from './api/bureaus'
import type { TransmissionResult } from './api/transmissions'
import { bytes as formatBytes, count as formatCount, shortId } from './format'
import { COUNSEL_MEMO } from './mandatory-copy'

export type RegisterNo =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 19
  | 20
  | 21
  | 22
  | 23
  | 24
  | 25
  | 26
  | 27
  | 28
  | 29
  | 30
  | 31
  | 32
  | 33
  | 34
  | 35
  | 36
  | 37

/** The decision that approved an entry (the mission 6 decision sheet). */
export const OWNER_DECISION = 'owner decision 2026-09-26'

/** The owner's approval of every mission 8 register 34 and 35 entry, the copy owner agreeing (2026-09-28). */
export const OWNER_DECISION_M08 = 'owner decision 2026-09-28'

/** The copy owner's reading of the two claim-adjacent mission 8 entries against the T.3 ceiling. */
const M08_CEILING_REVIEWED = { ceilingReviewed: 'reviewer: copy owner (owner decision 2026-09-28)' } as const

interface CopyMeta {
  owner: 'copy'
  status: 'provisional' | 'approved'
  /** Only on an approved entry: the decision that approved it. */
  decision?: typeof OWNER_DECISION | typeof OWNER_DECISION_M08
  register: RegisterNo
  /**
   * A decision id (`DM-…`) or `reviewer: <name>`: the entry was read against the T.3 ceiling; or
   * `pending: copy owner`: a claim-adjacent entry nobody has read yet (mission 7 squad U12).
   */
  ceilingReviewed?: string
  /** An owner outside copy who must rule on this sentence (mission 7 OR-11). */
  rulingBy?: ReadonlyArray<'legal' | 'design' | 'security'>
  /** Mission 10: the Legal open questions that must approve this sentence before GA (spec "Copy register"). */
  legal?: ReadonlyArray<'Q14' | 'Q15'>
  /** Mission 10: the decision that gates this sentence on the owner (spec amendment 3). */
  ownerGate?: string
  /** Counsel's provisional source of this sentence: the memo's ask and `COUNSEL_MEMO` (pending partner review). */
  counsel?: { ask: CounselAsk; source: typeof COUNSEL_MEMO }
}

/** The asks of counsel's memo (2026-10-03) whose copy lives in the register. */
export type CounselAsk = 'B6' | 'C2' | 'C3' | 'C4' | 'C5' | 'C6' | 'C7'

export type CopyEntry = CopyMeta & ({ text: string } | { render: (args: never) => string })

/** A count as a number, or as a string the caller already formatted. */
export type Count = number | string

function n(value: Count): string {
  return typeof value === 'number' ? formatCount(value) : value
}

function size(value: Count): string {
  return typeof value === 'number' ? formatBytes(value) : value
}

// mission 6 WP13: named so dropzoneCaption can say "first sheet only" for it (handoff from WP06).
const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const TYPE_LABELS: Record<string, string> = {
  'text/csv': 'CSV',
  [XLSX_TYPE]: 'XLSX',
  'text/plain': 'TXT',
  'application/octet-stream': 'DAT',
}

/** `CSV or XLSX` from the layout document's `limits.types`. */
function typeList(types: readonly string[]): string {
  const labels = types.map((t) => TYPE_LABELS[t] ?? t)
  if (labels.length <= 1) return labels.join('')
  return `${labels.slice(0, -1).join(', ')} or ${labels.at(-1)}`
}

// owner decision 2026-09-26 (decision 1): a terminal run has no files to download, and says why in the
// smallest grammatical form; every other state is "not ready yet".
const TERMINAL_REASONS: Record<string, string> = {
  cancelled: 'it was cancelled',
  failed: 'it failed',
  rejected: 'it was rejected',
}

/** The Path B file as the dropzone names it (owner decision 2026-09-26, decision 7). */
const PATH_B_FILE = 'Metro 2 character file (.txt, .dat, .metro2 or no extension)'

/** Mission 7: a claim-adjacent entry the copy owner has not read against the T.3 ceiling (squad U12). */
const PENDING_CEILING = { ceilingReviewed: 'pending: copy owner' } as const

/** Mission 9 (S18): claim-adjacent, unread, and Compliance reads it with the copy owner. */
const PENDING_CEILING_COMPLIANCE = { ceilingReviewed: 'pending: copy owner, Compliance' } as const

/**
 * Mission 10 (spec "Copy register"): a period or contract sentence for Legal (Q14). Counsel's memo replaced every
 * other Q14 and Q15 sentence (`counsel`); `deletionExportFirst` has no counsel text of its own.
 */
const Q14 = { legal: ['Q14'] } as const

/** Counsel's memo (2026-10-03), provisional pending partner review: the entry's text is the ask's. */
function counsel(ask: CounselAsk) {
  return { counsel: { ask, source: COUNSEL_MEMO } } as const
}

/** Mission 10 (spec amendment 3): the encryption statement waits on the owner's real KMS decision. */
const KMS_GATE = { ownerGate: 'DM-01M3VYK5GMZDXY4RGS9MHN0PS7' } as const

/** `1 finding`, `2 findings`: the singular at 1 (mission 7 squad U8). */
function plural(value: Count, one: string, many: string): string {
  return `${n(value)} ${value === 1 || value === '1' ? one : many}`
}

/** `Experian and TransUnion`, `Equifax, TransUnion and Innovis`. */
function bureauList(bureaus: readonly Bureau[]): string {
  const names = bureaus.map(bureauName)
  if (names.length <= 1) return names.join('')
  return `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`
}

/** The words of a transmission result, shared by `resultLabel.*` and every render that names a result. */
const RESULT_LABELS: Record<TransmissionResult, string> = {
  pending: 'Sending',
  success: 'Success',
  auth_failed: 'Auth failed',
  timeout: 'Timeout',
  not_sent: 'Not sent',
}

function t(register: RegisterNo, text: string) {
  return { owner: 'copy', status: 'provisional', register, text } as const
}

function r<A>(register: RegisterNo, render: (args: A) => string) {
  return { owner: 'copy', status: 'provisional', register, render } as const
}

/** An approved `text` entry (owner decision 2026-09-26). */
function ta(register: RegisterNo, text: string) {
  return { owner: 'copy', status: 'approved', decision: OWNER_DECISION, register, text } as const
}

/** An approved `render` entry (owner decision 2026-09-26). */
function ra<A>(register: RegisterNo, render: (args: A) => string) {
  return { owner: 'copy', status: 'approved', decision: OWNER_DECISION, register, render } as const
}

/** A mission 8 `text` entry the owner approved (owner decision 2026-09-28). */
function tm(register: 34 | 35, text: string) {
  return { owner: 'copy', status: 'approved', decision: OWNER_DECISION_M08, register, text } as const
}

/** A mission 8 `render` entry the owner approved (owner decision 2026-09-28). */
function rm<A>(register: 34 | 35, render: (args: A) => string) {
  return { owner: 'copy', status: 'approved', decision: OWNER_DECISION_M08, register, render } as const
}

export const PROVISIONAL_COPY = {
  // 1. The dropzone (sheet 11 callout 2; FR-008, U.3). Every limit comes from the layout document.
  // mission 6 WP13: handoff from WP06: "first sheet only" when XLSX is accepted (US3 scenario 2, §7.15).
  dropzoneCaption: r(1, (a: { types: readonly string[]; admissionCap: Count; bytes: Count }) =>
    `${typeList(a.types)}, up to ${n(a.admissionCap)} records and ${size(a.bytes)}` +
      `${a.types.includes(XLSX_TYPE) ? ', first sheet only' : ''}.`,
  ),
  dropzoneRefusal: r(1, (a: { types: readonly string[]; bytes: Count }) =>
    `This file can't be used. Choose a ${typeList(a.types)} file of ${size(a.bytes)} or less.`,
  ),
  // owner decision 2026-09-26 (decision 7): the Path B caption names every accepted file and counts base
  // segments; the refusal names the same file (worded to match, provisional).
  dropzoneCaptionPathB: ra(1, (a: { admissionCap: Count; bytes: Count }) =>
    `A ${PATH_B_FILE}, up to ${n(a.admissionCap)} base segments and ${size(a.bytes)}.`,
  ),
  dropzoneRefusalPathB: r(1, (a: { bytes: Count }) =>
    `This file can't be used. Choose a ${PATH_B_FILE} of ${size(a.bytes)} or less.`,
  ),

  // 2. The Path B card (sheet 11 callout 7; U.4, X.10).
  pathBRules: r(2, (a: { admissionCap: Count; pathBBytes: Count }) =>
    `Metro 2 in character format only; packed files are refused. Up to ${n(a.admissionCap)} base segments and ` +
      `${size(a.pathBBytes)}. The structural pre-check is free, and analysis uses one credit per base segment. ` +
      'A file analyzed here can never be transmitted from Trellis.',
  ),
  /** The line before the screen's "browse" control. */
  // mission 6 WP13: handoff from WP06: Path B counts base segments, not records (as pathBRules says).
  pathBDropzoneBody: r(2, (a: { admissionCap: Count; pathBBytes: Count }) =>
    `Drop a Metro 2 file in character format here (up to ${n(a.admissionCap)} base segments and ${size(a.pathBBytes)}), or`,
  ),
  pathBNotTransmissible: t(2, 'Trellis did not generate this file, so it can never be transmitted from Trellis.'),

  // 3. The over-cap refusal (P.4); the API's detail is shown beneath it.
  // mission 6 WP13: handoff from WP06: the refusal states that nothing was charged (US3 scenario 4).
  // owner decision 2026-09-26 (decision 6): Path B passes `unit: 'base segments'` (worded to match, provisional).
  overCap: r(3, (a: { recordCount: Count; isMinimum: boolean; admissionCap: Count; unit?: 'records' | 'base segments' }) =>
    `This file has ${a.isMinimum ? 'more than ' : ''}${n(a.recordCount)} ${a.unit ?? 'records'}. A run accepts up to ` +
      `${n(a.admissionCap)} ${a.unit ?? 'records'}. Nothing was charged. Split the file and start one run for each part.`,
  ),

  // 4. The estimate panel (sheet 11 callouts 8 and 10; GEN.4, GEN.5, GEN.7, TX.8).
  // owner decision 2026-09-26 (decision 17): rendered as style guide §8's list through freeItemsList(), wording unchanged.
  freeItems: ta(
    4,
    'Always free: layout validation, the analysis of every file Trellis generates, transmission, and any run that is cancelled or fails.',
  ),
  reservedUntilReady: t(
    4,
    'Credits are reserved when the run starts and consumed only when it is ready. A cancelled or failed run returns them.',
  ),
  // owner decision 2026-09-26 (decision 2): the second sentence is dropped.
  typicalTimeAtCap: ra(4, (a: { admissionCap: Count }) =>
    `A file of ${n(a.admissionCap)} records typically finishes within two minutes of upload.`,
  ),

  // 5. The blocked band's remedy and the soft-cap caption (B.3, B.5); the API's message stays verbatim.
  buyCreditsRemedy: t(5, 'Buy credits'),
  softCapCaption: t(5, 'Your first billing cycle allows runs past your allowance. The estimate above says what this run adds.'),

  // 6. Sheet 12, rejected (U.2, U.4, D.2).
  rejectedStatement: t(
    6,
    'Layout validation is free, so this run consumed no credits. The analyzer did not run, so there are no ERROR, WARN or INFO findings.',
  ),
  // owner decision 2026-09-26 (decision 6): the Path B variant, after the structural header.
  rejectedStatementPathB: ta(
    6,
    'The structural check is free, so this run consumed no credits. The analyzer did not run, so there are no findings.',
  ),
  retentionClassNote: t(
    6,
    'The counts above stay with the run history. The row-level errors below are removed with the run under your retention setting.',
  ),
  pathBStructuralHeader: t(6, 'The structural pre-check refused this file before analysis. Nothing was charged.'),
  versionMismatch: t(
    6,
    'The layout version you declared does not match the file. The declared version decides, so the file was rejected and nothing was charged.',
  ),
  truncationFooterPathA: r(6, (a: { shown: Count; total: Count }) =>
    `Showing ${n(a.shown)} of ${n(a.total)} errors. The error report lists every one.`,
  ),
  truncationFooterPathB: t(6, 'Only the first structural errors are listed.'),

  // 7. Sheet 13, the run in progress (GEN.4, GEN.5, D.8, NF.2).
  'stage.validation': t(7, 'Validation'),
  'stage.preCheck': t(7, 'Pre-check'),
  'stage.generation': t(7, 'Generation'),
  'stage.analysis': t(7, 'Analysis'),
  'stage.ready': t(7, 'Ready'),
  'sublabel.free': t(7, 'free'),
  'sublabel.perRecord': t(7, '1 credit per record'),
  // owner decision 2026-09-26 (decision 6): Path B charges per base segment (worded to match, provisional).
  'sublabel.perBaseSegment': t(7, '1 credit per base segment'),
  'sublabel.reserved': t(7, 'reserved'),
  'sublabel.consumed': t(7, 'consumed'),
  queuedSentence: t(
    7,
    'Your run is accepted and queued. It is kept safely until a worker picks it up, and cancelling it is free.',
  ),
  'failure.parked': t(7, 'Trellis could not finish this run after several attempts.'),
  'failure.uploadObjectChanged': t(7, 'The uploaded file changed after it was checked, so the run stopped.'),
  'failure.generationEmpty': t(7, 'Generation produced no records from this file.'),
  'failure.analysisTimeout': t(7, 'The analysis did not finish in time.'),
  'failure.unknown': t(7, 'Trellis could not finish this run.'),
  supportReference: r(7, (a: { runId: string }) => `If you contact support, quote this reference: ${a.runId}`),
  nothingConsumed: t(7, 'Nothing was consumed; the reservation was returned.'),
  appliesAllRules: r(7, (a: { ruleCount: Count }) =>
    `Analysis applies all ${n(a.ruleCount)} rules of the rule set in effect when the run started. It is free and cannot be skipped.`,
  ),
  'cancelDialog.title': t(7, 'Cancel this run?'),
  'cancelDialog.body': t(7, 'Cancelling discards any partial output and returns the reserved credits. Nothing is consumed.'),
  'cancelDialog.keep': t(7, 'Keep running'),

  // 8. Truncation and completeness (P.8; DM-01M39PR43C8WYP2S7T8VYTYTZE).
  // owner decision 2026-09-26 (decision 5): takes inWindow; the screen appends no "(N listed)".
  truncationBand: ra(8, (a: { segment: string; count: Count; inWindow: Count; listedThrough: Count }) =>
    `${a.segment} has ${n(a.count)} findings; ${n(a.inWindow)} are listed here, through line ${n(a.listedThrough)}. The CSV export has all of them.`,
  ),
  byRecordCompleteness: r(8, (a: { completeBeforeLine: Count | null }) =>
    a.completeBeforeLine === null
      ? 'Some records may have findings that are not listed here. The CSV export is the complete list.'
      : `Records before line ${n(a.completeBeforeLine)} are complete. Later records may have findings that are not listed here; the CSV export is the complete list.`,
  ),
  mayBeIncomplete: t(8, 'may be incomplete'),
  pageLocalFilterNote: t(8, 'Rule, field, segment and text filters apply to the rows loaded on this page.'),
  chipsDisabledReason: t(8, 'Severity filters apply to the By rule tab.'),
  notInWindow: t(8, 'No records of this rule are listed in the views. The CSV export has every one.'),

  // 9. Values (DM-01M39PR6WCD3792M8TVM59WYJ0; TEN.10).
  valueWithheld: t(
    9,
    'Value withheld. Trellis shows no value for a finding about an invalid code on a record whose values are protected.',
  ),
  valueNotLocated: t(9, 'Value not located in the record.'),
  formulaGuardNote: t(
    9,
    'In the CSV export, a value that starts like a spreadsheet formula carries a leading apostrophe, for example \'-5, so it is not run as one.',
  ),

  // 10. Table labels (ANA.2, ANA.5, ANA.9).
  noAccountNumber: t(10, 'no account number'),
  findingsColumn: t(10, 'Findings'),
  fileLevelRow: t(10, 'File-level (line 0)'),
  'trailer.consistent': t(10, 'Matches'),
  'trailer.mismatch': t(10, 'Does not match'),
  ruleSetVersionLabel: t(10, 'Current rule set'),
  allSegments: t(10, 'All segments'),
  // mission 6 WP13: handoff from WP03, WP07, WP08 and WP10: the run fact labels RunHeader, RunDetailsCard,
  // StageLog and the sheet 23 context each spelled; worded as they rendered.
  'fact.runId': t(10, 'Run ID'),
  'fact.input': t(10, 'Input'),
  'fact.path': t(10, 'Path'),
  'fact.layout': t(10, 'Layout'),
  'fact.ruleSet': t(10, 'Rule set'),
  'fact.records': t(10, 'Records'),
  'fact.credits': t(10, 'Credits'),
  'fact.started': t(10, 'Started'),
  'fact.finished': t(10, 'Finished'),
  // owner decision 2026-09-26 (decision 6): a Path B run's record count is its base segments (provisional).
  'fact.baseSegments': t(10, 'Base segments'),
  // owner decision 2026-09-26 (decision 4): the by-rule footer counts rows. `total` is null when no truthful
  // row total exists (the by-rule body carries none; the cursor state gives one on the last page only).
  byRuleFooter: ra(10, (a: { start: Count; end: Count; total: Count | null }) =>
    `Showing rows ${n(a.start)}–${n(a.end)}${a.total === null ? '' : ` of ${n(a.total)}`}`,
  ),

  // 11. Explanations (ANA.6).
  placeholderExplanation: t(11, 'No authored explanation for this rule yet. The rule text and its CRRG reference still apply.'),
  explanationUnavailable: t(11, 'The explanation could not be loaded. Try again in a moment.'),
  placeholderMark: t(11, 'No explanation yet'),

  // 12. Downloads (the export busy sentence is the API's detail, rendered as given).
  downloadIncomplete: t(12, 'The download did not complete, so nothing was saved. Try again.'),

  // 13. Sheet 30 and retention (D.1, D.2, A.5).
  auditLogNotice: t(13, 'Every download is recorded in your audit log with who downloaded which file and when.'),
  'fileRow.generated': t(13, 'The Metro 2 file exactly as it would be transmitted. Its SHA-256 lets you show what you delivered.'),
  'fileRow.upload': t(13, 'The file you uploaded, kept so you can reconcile source rows against the generated records.'),
  'fileRow.findingsCsv': t(13, 'Every finding of this run, one row each.'),
  pathBNotGenerated: t(13, 'Trellis did not generate a file for this run: it analyzed the Metro 2 file you uploaded.'),
  retentionLine: r(13, (a: { expiresAt: string }) =>
    `The upload, the files and the record-level findings are removed on ${a.expiresAt} under your retention setting.`,
  ),
  removedUnderRetention: t(13, 'This file was removed under your retention setting.'),

  // 14. Sheet 23, report a bureau rejection (ANA.11, X.1, X.9, T.3).
  // mission 6 WP13: handoff from WP10: superseded by rejectionPurposeWithCount, which carries the rule count;
  // no production caller. Kept because rejectionPurposeWithCount's test pins that it keeps this sentence.
  rejectionPurpose: t(
    14,
    'Tell Trellis when a bureau rejects a file it generated. Reports help decide which rules to add or reclassify in the rule catalog.',
  ),
  rejectionHelper: t(
    14,
    'Paste the rejection as you received it. Include record and error codes; leave out names, account numbers and other consumer data.',
  ),
  deliveredByHand: t(14, 'Not transmitted from Trellis: I delivered the file myself.'),
  // owner decision 2026-09-26 (decision 3): no change log is promised.
  whatHappensNext: ta(14, 'Keep this report number. If a rule changes because of your report, we will reference it.'),
  // owner decision 2026-09-26 (decision 13): approved as written.
  whatBloomWillNotDo: {
    ...ta(
      14,
      'Bloom will not review your file, contact the bureau on your behalf, or handle a dispute. You stay responsible for what you report.',
    ),
    ceilingReviewed: 'DM-01M3C8MGK4F8V0M89K52YPHJ39',
  },
  consentLabel: r(14, (a: { email: string }) => `Trellis may email ${a.email} about this report.`),
  alteredAfterDownloadLabel: t(14, 'The file was changed after it was downloaded from Trellis.'),
  rejectionRunNotReady: t(14, 'You can report a rejection once this run is ready.'),
  rejectionPurposeFallback: t(14, 'Tell Trellis when a bureau rejects a file it generated, so the rule catalog can improve.'),
  confirmationClose: t(14, 'Close'),
  // mission 6 WP10 (cycle 2, orchestrator-authorised additive edit): rejectionPurpose's sentence with the
  // rule count from ['rules'] (US10.1, research §1.22), and Submit's disabled reason. Nothing renamed.
  rejectionPurposeWithCount: r(14, (a: { ruleCount: Count }) =>
    `Tell Trellis when a bureau rejects a file it generated. Reports help decide which rules to add or reclassify in the rule catalog of ${n(a.ruleCount)} rules.`,
  ),
  chooseBureauFirst: t(14, 'Choose the bureau that rejected the file first.'),
  // mission 6 WP13: handoff from WP10: the X.10 band's own key.
  // owner decision 2026-09-26 (decision 8): re-worded for sheet 23.
  rejectionPathBNotGenerated: ta(
    14,
    'Rejection reports are for files Trellis generated. This run analyzed a Metro 2 file you uploaded.',
  ),

  // 15. Sheet 40 and the static header card (GEN.3, GEN.6, D.3).
  headerStorageStatement: t(
    15,
    'Program identifiers are encrypted with a key for your organization and shown masked. Changes are recorded in your audit log.',
  ),
  staticVersusPerRun: t(
    15,
    'These values are written into the header of every file. The activity date, date created and cycle identifier are set per run instead.',
  ),
  entered: t(15, 'Entered'),
  notEntered: t(15, 'Not entered'),
  notEnteredGen6: t(15, 'Files are still generated, but they are marked not deliverable to this bureau and cannot be transmitted.'),
  provenance: r(15, (a: { updatedAt: string }) => `Last changed ${a.updatedAt}.`),
  'perRunDefault.activityDate': t(15, 'Leave empty to use today in UTC.'),
  'perRunDefault.dateCreated': t(15, 'Leave empty to use today in UTC.'),
  'perRunDefault.cycleIdentifier': t(15, 'Two letters or digits. Leave empty to use the activity month.'),
  // mission 6 WP13: handoff from WP05: the reporter card's h3, the canonical term (CLAUDE.md §3.7).
  headerReporterCardTitle: t(15, 'Credit data reporter'),

  // 16. Empty, not-found and error states.
  historyEmptyTitle: t(16, 'No runs yet'),
  historyEmptyBody: t(16, 'Start a run from a spreadsheet in the Trellis layout, or analyze a Metro 2 file you already have.'),
  notFound: t(16, 'This page does not exist, or it is not part of your organization.'),
  loading: t(16, 'Loading…'),
  genericError: t(16, 'Something went wrong. Try again.'),
  layoutUnreachable: t(16, 'Trellis could not load the file limits, so uploads are paused. Try again.'),
  uploadExpired: t(16, 'The upload expired. Choose the file again.'),
  storageFailed: t(16, 'The upload did not reach storage. Try again.'),
  uploadAgain: t(16, 'Upload the file again'),
  alreadyUsed: t(16, 'This upload was already used'),
  reloadAndRetry: t(16, 'Reload the page and try again'),
  // owner decision 2026-09-26 (decision 1): in-flight states are "not ready yet"; terminal ones say why.
  runNotReady: ra(16, (a: { state?: string }) => {
    const reason = a.state === undefined ? undefined : TERMINAL_REASONS[a.state]
    return reason === undefined ? 'This run is not ready yet.' : `This run has no files to download: ${reason}.`
  }),
  zeroFindingsTitle: t(16, 'No findings'),
  zeroFindingsBody: t(16, 'The analyzer found nothing to report in this file.'),
  noRunsMatch: t(16, 'No runs match these filters.'),
  'historyEmptyLinks.layoutSpec': t(16, 'Read the layout spec'),
  'historyEmptyLinks.headerParams': t(16, 'Enter header parameters'),
  catalogNoMatchTitle: t(16, 'No rules match'),
  catalogNoMatchBody: t(16, 'Change the filters or the search to see more rules.'),
  unknownRule: t(16, 'This rule is not in the current rule set.'),
  // mission 6 WP13: handoff from WP08: the results filtered-empty pair, worded as the catalog pair it borrowed.
  resultsNoMatchTitle: t(16, 'No rules match'),
  resultsNoMatchBody: t(16, 'Change the filters or the search to see more rules.'),
  // Mission 6 review follow-ups 14, 15 and 23 (A-1, A-2, A-6): session expiry, a run read that
  // stopped refreshing, and the /app not-found and render-error pages.
  // owner decision 2026-09-26 (decision 12): the seven are approved as written.
  sessionExpired: ta(16, 'Your session ended. Log in again to continue.'),
  pollInterrupted: ta(16, 'Trellis could not refresh this run. The status below is the last one it read.'),
  retry: ta(16, 'Try again'),
  notFoundTitle: ta(16, 'Page not found'),
  renderErrorTitle: ta(16, 'This page could not be shown'),
  renderError: ta(16, 'Trellis could not show this page. Go back to your runs and try again.'),
  backToRuns: ta(16, 'Go to runs'),
  // owner decision 2026-09-26 (decision 9): was the inline "Start again" on a refused cursor (results, drawer, history).
  backToFirstPage: ta(16, 'Back to the first page'),
  // owner decision 2026-09-26 (decision 10): was the inline "Only v1 is accepted:"; the accepted versions follow in mono.
  layoutVersionUnsupported: ta(16, "This layout version isn't supported. Use v1:"),

  // 17. Breadcrumb segments and the run label.
  'crumb.runs': t(17, 'Runs'),
  'crumb.newRun': t(17, 'New run'),
  'crumb.download': t(17, 'Download'),
  'crumb.ruleCatalog': t(17, 'Rule catalog'),
  'crumb.settings': t(17, 'Settings'),
  'crumb.organization': t(17, 'Organization'),
  'crumb.headerParameters': t(17, 'Header parameters'),
  'crumb.transmit': t(17, 'Transmit'),
  'crumb.transmissions': t(17, 'Transmission log'),
  'crumb.subscribe': t(17, 'Subscribe'),
  'crumb.bureaus': t(17, 'Bureau credentials'),
  'crumb.data': t(17, 'Data retention'),
  'crumb.audit': t(17, 'Audit log'),
  'crumb.billing': t(17, 'Billing'),
  runLabel: r(17, (a: { runId: string }) => `Run ${shortId(a.runId)}`),
  // owner decision 2026-09-26 (decision 11): times stay in UTC.
  timesInUtc: ta(17, 'Times in UTC'),

  // 18. The avatar menu.
  signOut: t(18, 'Sign out'),
  avatarName: r(18, (a: { email: string }) => `Account menu for ${a.email}`),

  // Mission 7 (WP07 T042). Register 10, F2: the results header meta line; the count renders beside it in mono.
  'fact.firedRules': t(10, 'Rules fired'),

  // 19. Sheet 41's handling statement (41/2; TX.1, TEN.4, TEN.11, A.5). The per-organization key at rest is a
  // security claim (OR-11, squad S17).
  bureauHandlingStatement: {
    ...t(
      19,
      'Bureau passwords and keys are encrypted with a key for your organization, shown masked, and never written to a log. Every change and every connection test is recorded in your audit log.',
    ),
    rulingBy: ['security'],
    ...PENDING_CEILING,
  },

  // 20. Status badges and their meanings (41/3, 41/6, 41/10; TX.2, TX.9, X.2). Not entered reuses `notEntered`.
  'credStatus.enteredUntested': t(20, 'Entered, never tested'),
  // The badge's date is optional (BureauStatusBadge `verifiedAt?`, contracts/ui-api.md).
  'credStatus.verified': r(20, (a: { date?: string }) => (a.date === undefined ? 'Verified' : `Verified ${a.date}`)),
  'credStatus.authFailed': t(20, 'Auth failed'),
  'credStatus.disabledByTrellis': t(20, 'Disabled by Trellis'),
  'credStatusMeaning.verified': {
    ...t(
      20,
      'A login to the bureau host under your own credentials succeeded. That login is the ownership check; nothing was uploaded.',
    ),
    ...PENDING_CEILING,
  },
  'credStatusMeaning.authFailed': t(
    20,
    'The bureau host refused these credentials. Transmission to this bureau is off until a test succeeds; you can still download the file and deliver it yourself.',
  ),

  // 21. Not entered and entered-untested (41/7, 41/8; X.1). bureauNotEntered is the card sentence with the X.1
  // statement; rowReason.notEntered is the transmit row's reason with its link (kept distinct, squad U12).
  bureauNotEntered: {
    ...t(
      21,
      'No third party obtains subscriber codes or bureau access for you. Until you enter your own credentials, download the file and deliver it to this bureau yourself.',
    ),
    ...PENDING_CEILING,
  },
  bureauEnteredUntested: t(
    21,
    'Your credentials are stored, but transmission to this bureau is not offered until a connection test succeeds.',
  ),

  // 22. Test connection results (41/6, 41/10; TX.2, X.2, AV.7, SEC.1, SEC.6). testUnavailable never names MGTS.
  testing: t(22, 'Testing the connection…'),
  testSucceeded: {
    ...t(22, 'The connection succeeded under your own credentials. Nothing was uploaded.'),
    ...PENDING_CEILING,
  },
  testAuthFailed: t(22, 'Authentication failed. The bureau host refused these credentials.'),
  testTimedOut: t(22, 'The bureau host did not answer in time. Check the host and port, then test again.'),
  testUnavailable: t(22, 'The delivery service is unavailable, so the test did not run. Try again in a few minutes.'),
  testNotApplied: t(22, 'The credentials changed while the test ran, so its result was not applied. Test again.'),
  // `minutes` is `retry_after_seconds` rounded up; without it the sentence names no time.
  testRateLimited: r(22, (a: { minutes?: Count }) =>
    a.minutes === undefined
      ? 'Too many connection tests for this bureau. Try again in a few minutes.'
      : `Too many connection tests for this bureau. Try again in ${plural(a.minutes, 'minute', 'minutes')}.`,
  ),
  testHostNotAllowed: t(
    22,
    'This host is not allowed. It must be a public DNS name that resolves outside private and reserved networks.',
  ),

  // 23. Fields and the secret (41/4, 41/9; TX.1, TEN.4, FR-001, FR-019; DM-01M3EZ6VTRWJYNTG7F3SSYX61V; owner
  // decision A2). Server field errors map to fieldInvalid.*, key* and chooseSecret by (field, rule) (squad U27).
  'field.host': t(23, 'Host'),
  'field.port': t(23, 'Port'),
  'field.username': t(23, 'Username'),
  'field.remotePath': t(23, 'Remote path'),
  secretKindLegend: t(23, 'Authentication method'),
  'secretKind.password': t(23, 'Password'),
  'secretKind.privateKey': t(23, 'SSH private key'),
  showPassword: t(23, 'Show password'),
  hidePassword: t(23, 'Hide password'),
  passwordStored: t(23, 'Stored password'),
  usernameStored: t(23, 'Stored username'),
  usernameKeepHint: t(23, 'Leave empty to keep the stored username.'),
  // A fixed mask with no tail: charter TRELLIS-012 forbids a secret fragment (analysis I14).
  passwordMask: t(23, '••••••••'),
  neverShownAgain: t(23, 'Never shown again'),
  keyFingerprintLabel: t(23, 'Key fingerprint'),
  chooseKeyFile: t(23, 'Choose a key file'),
  pasteKeyLabel: t(23, 'Or paste the private key'),
  keyTooLarge: t(23, 'This key is too large. A private key file is at most 16 KiB.'),
  keyEncrypted: t(23, 'This key is protected by a passphrase. Trellis needs a key without one.'),
  keyMalformed: t(23, 'Trellis cannot read this key. Use a private key in PEM or OpenSSH format.'),
  chooseSecret: t(23, 'Enter either a password or a private key.'),
  // Mission 7 review follow-up 1 (SEC-1; owner decision 2026-09-26): shown while the host or port differs from the
  // stored one, and for the server's (secret, required) refusal. Provisional; for the copy owner.
  secretRequiredOnMove: t(23, 'The host or port changed, so enter the password or private key again. A stored secret is never sent to a different host.'),
  'fieldInvalid.host': t(23, 'Enter the host as a lower-case DNS name. IP addresses are not accepted.'),
  'fieldInvalid.hostNotAllowed': t(23, 'This host resolves to a private or reserved address, so it is not allowed.'),
  'fieldInvalid.port': t(23, 'This port is not allowed for bureau delivery.'),
  'fieldInvalid.username': t(23, 'Enter a username of 1 to 128 printable characters.'),
  'fieldInvalid.remotePath': t(23, 'Enter a path that starts with a slash, with no control characters.'),
  'fieldInvalid.password': t(23, 'Enter a password of 1 to 256 characters, with no control characters.'),
  replacePassword: t(23, 'Replace password'),
  replaceKey: t(23, 'Replace key'),
  saveCredentials: t(23, 'Save credentials'),
  testConnection: t(23, 'Test connection'),
  credentialsSaved: t(23, 'Credentials saved. Test the connection before you transmit.'),

  // 24. The subscriber code on the card (41/5; GEN.3). The value renders masked, with no reveal.
  subscriberCodeLabel: t(24, 'Subscriber code'),
  editInHeaderParameters: t(24, 'Edit in Header parameters'),

  // 25. The terms card and the blocked band (41/11, 41/12, 31/2, 31/13; TX.3). "Who" is the tenant's contact email.
  termsCardTitle: t(25, 'Processing terms'),
  termsNotAccepted: t(25, 'Not accepted. Accept the processing terms before your first transmission.'),
  termsAccepted: r(25, (a: { version: string; time: string; email: string }) =>
    `Accepted version ${a.version} on ${a.time} by ${a.email}.`,
  ),
  termsNewVersion: r(25, (a: { acceptedVersion: string; time: string }) =>
    `A new version needs your acceptance. You accepted version ${a.acceptedVersion} on ${a.time}.`,
  ),
  termsSummaryHeading: t(25, 'What you agree to'),
  termsAccept: t(25, 'Read and accept'),
  termsIAccept: t(25, 'I accept'),
  termsBlockedRemedy: t(25, 'Accept the processing terms to transmit.'),

  // 26. The transmit page (sheet 31; TX.2, TX.4, TX.6, TX.8, TX.9, GEN.6, AV.7; OR-5). progressQueued never names MGTS.
  gateEligible: t(26, 'No ERROR findings. This file can be transmitted.'),
  gateAckRequired: r(26, (a: { count: Count }) =>
    `${plural(a.count, 'ERROR finding', 'ERROR findings')} — acknowledgment required`,
  ),
  // TX.8 ("not charged"), US4 scenario 3, sheet 31 callout 10: no credits, and therefore no estimate.
  tx8Band: t(26, 'Transmission is not charged: it uses no credits, so there is no estimate.'),
  'rowReason.enteredUntested': t(26, 'Entered, never tested. Test the connection before you transmit to this bureau.'),
  'rowReason.notEntered': t(26, 'No credentials entered. Download the file and deliver it yourself, or enter your credentials.'),
  'rowReason.authFailed': t(26, 'The last login failed. Test the connection again before you transmit to this bureau.'),
  downloadAndDeliverLink: t(26, 'Download and deliver yourself'),
  transmitToN: r(26, (a: { n: Count }) => `Transmit to ${plural(a.n, 'bureau', 'bureaus')}`),
  /** The per-row checkbox's accessible name (squad C11). */
  transmitSelectBureau: r(26, (a: { bureau: Bureau }) => `Transmit to ${bureauName(a.bureau)}`),
  selectAtLeastOne: t(26, 'Select at least one verified bureau.'),
  progressSending: t(26, 'Sending the file…'),
  progressQueued: t(
    26,
    'Still waiting for the delivery service. The file is sent when it becomes available, and you can leave this page.',
  ),
  // Mission 7 review follow-up 6 (UI-1; FR-011, NFR-008): a poll that failed after the 202. The rows are the last
  // status read, never "still waiting"; the band links to the run's log. Provisional; for the copy owner.
  transmitPollInterrupted: t(
    26,
    'Trellis could not refresh the delivery status. The rows below show the last status it read; the transmission log has the current results.',
  ),
  lastTransmission: r(26, (a: { time: string; result: TransmissionResult }) =>
    `Last transmission ${a.time}: ${RESULT_LABELS[a.result]}`,
  ),
  lastTransmissionUncertain: t(
    26,
    'The last attempt timed out, so the bureau may already have the file. Check with the bureau before you send it again.',
  ),
  viewTransmissionLog: t(26, 'View the transmission log'),
  transmitNotDeliverable: t(
    26,
    'This file is not deliverable: a program identifier was missing when it was generated, so it cannot be transmitted.',
  ),
  notDeliverableRemedy: t(26, 'Enter the missing program identifier in Header parameters, then start a new run.'),
  testConnectionFirst: t(26, 'Test the connection first'),
  // The problem remedy (credential-not-entered); bureauNotEntered is the card sentence (kept distinct, squad U12).
  enterCredentialsFirst: t(26, "Enter this bureau's credentials first"),
  editCredentials: t(26, 'Edit the credentials'),
  hostNotAllowedRemedy: t(26, 'The stored host is not allowed. Edit the credentials and enter a public host.'),
  transmissionInProgress: t(
    26,
    'A transmission of this file to this bureau is still in progress. Its result will appear in the transmission log.',
  ),
  ackCountChanged: t(26, 'The number of ERROR findings changed. Read the new sentence and type it again.'),
  'transmitCol.bureau': t(26, 'Bureau'),
  'transmitCol.status': t(26, 'Status'),
  'transmitCol.subscriberCode': t(26, 'Subscriber code'),
  'transmitCol.remotePath': t(26, 'Remote path'),
  'transmitCol.remoteFilename': t(26, 'Remote filename'),
  'transmitCol.lastTransmission': t(26, 'Last transmission'),
  // mission 7 WP14: J3 (orchestrator-authorised join scope; squad P22; WP10 deviations 1 and 10). The heading
  // Progress focuses after the 202, until now crumb.transmissions; worded as it rendered.
  progressHeading: t(26, 'Transmission log'),
  // The gated Transmit label and its reason line (TransmitControl, sheet 31): style guide §7.1 as amended by the owner
  // decision, so approved; worded exactly as the literals they replace.
  transmitAcknowledge: ta(26, 'Transmit (acknowledge ERRORs)'),
  transmitAckReason: ra(26, (a: { count: Count }) => plural(a.count, 'ERROR finding', 'ERROR findings')),
  // The GEN.6 badge text (style guide §2 badge table), until now a literal in five screens.
  notDeliverableBadge: t(26, 'NOT DELIVERABLE'),

  // 27. The acknowledgment modal (sheet 33; TX.4, ANA.4; squad U8, U9). The sentence itself is ERROR_ACK in
  // mandatory-copy.ts.
  ackTitle: r(27, (a: { count: Count }) => `Transmit a file with ${plural(a.count, 'ERROR finding', 'ERROR findings')}?`),
  ackErrorRulesHeading: t(27, 'ERROR rules in this file'),
  ackMoreRules: r(27, (a: { n: Count }) =>
    a.n === 1 || a.n === '1' ? 'See 1 more rule in Results' : `See ${n(a.n)} more rules in Results`,
  ),
  // mission 7 WP14: J6 (WP10 review): the "more" link when the walk has more rules but their number is not known
  // (the count read failed or is pending, or a rule-set config moved a severity).
  ackMoreRulesUncounted: t(27, 'See more rules in Results'),
  explainRule: t(27, 'Explain'),
  hideExplanation: t(27, 'Hide explanation'),
  ackWhoWhenWhat: r(27, (a: { email: string; time: string; bureaus: readonly Bureau[] }) =>
    `Recorded in your audit log: ${a.email}, ${a.time}, sent to ${bureauList(a.bureaus)}.`,
  ),
  typeSentenceExactly: t(27, 'Type the sentence exactly as shown to continue.'),
  ackInputLabel: t(27, 'Acknowledgment'),
  correctedUpload: t(27, 'Or correct the source file and start a new run.'),
  transmitAnyway: t(27, 'Transmit anyway'),
  ackDisabledReason: t(27, 'Type the sentence exactly to enable Transmit anyway.'),
  modalCancel: t(27, 'Cancel'),

  // 28. The transmission log (sheet 32; TX.6, D.2, D.5; OR-5, squad U5, U10). Every detail.* never names MGTS.
  'logCol.time': t(28, 'Time'),
  'logCol.acting': t(28, 'Acting user'),
  'logCol.run': t(28, 'Run'),
  'logCol.bureau': t(28, 'Bureau'),
  'logCol.bytes': t(28, 'Bytes sent'),
  'logCol.remoteFilename': t(28, 'Remote filename'),
  'logCol.result': t(28, 'Result'),
  'logCol.actions': t(28, 'Actions'),
  'filter.run': t(28, 'Run'),
  'filter.bureau': t(28, 'Bureau'),
  'filter.result': t(28, 'Result'),
  'filter.from': t(28, 'From'),
  'filter.to': t(28, 'To'),
  'filter.search': t(28, 'Search'),
  // mission 7 WP14: WP11 deviation 2, the selects' no-filter item (a literal until now).
  'filter.all': t(28, 'All'),
  disabledSuffix: t(28, '— disabled'),
  notSentTo: r(28, (a: { bureaus: readonly Bureau[] }) => `Not sent to ${bureauList(a.bureaus)}.`),
  notSentToNone: t(28, 'Sent to every bureau.'),
  // The retention period is Legal's (sheet 32 callout 11; OR-11).
  class2Retention: {
    ...t(
      28,
      "The transmission log holds no consumer data. It is kept after the run's files are removed under your retention setting.",
    ),
    rulingBy: ['legal'],
  },
  retryTransmission: t(28, 'Retry'),
  retryConfirmTitle: t(28, 'Send this file again?'),
  retryConfirmBody: t(
    28,
    'This attempt timed out, so the bureau may already have received the file. Check with the bureau before you send it again.',
  ),
  retryConfirmAction: t(28, 'Send again'),
  reportRejectionRow: t(28, 'Report rejection'),
  logEmpty: t(28, 'No transmissions yet.'),
  logEmptyFiltered: t(28, 'No transmissions match these filters.'),
  allTransmissions: t(28, 'All transmissions'),
  nextPage: t(28, 'Next page'),
  previousPage: t(28, 'Previous page'),
  // The "Sending" label and its info tone, and the Not sent tone, are the design owner's (squad U10, P4).
  'resultLabel.pending': { ...t(28, RESULT_LABELS.pending), rulingBy: ['design'] },
  'resultLabel.success': t(28, RESULT_LABELS.success),
  'resultLabel.authFailed': t(28, RESULT_LABELS.auth_failed),
  'resultLabel.timeout': t(28, RESULT_LABELS.timeout),
  'resultLabel.notSent': { ...t(28, RESULT_LABELS.not_sent), rulingBy: ['design'] },
  'detail.authRejected': t(28, 'The bureau host refused the credentials.'),
  'detail.hostTimeout': t(28, 'The bureau host did not answer in time.'),
  'detail.interrupted': t(28, 'The session was interrupted, so the bureau may have received part or all of the file.'),
  'detail.mgtsUnavailable': t(28, 'The delivery service was unavailable, so nothing was sent.'),
  'detail.credentialChanged': t(28, 'The credentials changed after this transmission was queued, so nothing was sent.'),
  'detail.fileUnavailable': t(28, 'The generated file was no longer available, so nothing was sent.'),
  // Repeats killSwitchExplanation, so it waits for the copy owner with it (no rulingBy: that stays on five keys).
  'detail.bureauDisabled': {
    ...t(28, 'Trellis does not deliver to this bureau at present, so nothing was sent.'),
    ...PENDING_CEILING,
  },
  'detail.hostRefused': t(28, 'The stored host is not allowed, so nothing was sent.'),
  'detail.notAttempted': t(28, 'The transmission could not start, so nothing was sent.'),

  // 29. The kill switch (41/13, 31/12; TX.9, T.3, X.1; spec Assumptions "Kill-switch copy"). A deviation from the
  // drawn "explains why": it states no reason, no decider and no contact with the bureau (OR-11; Mike, Q9).
  killSwitchExplanation: {
    ...t(29, 'Trellis does not deliver to this bureau at present. You can download the file and deliver it yourself.'),
    rulingBy: ['design', 'legal'],
    ...PENDING_CEILING,
  },

  // 30. The rest of the app (sheet 30/8, 30/10; sheet 10/7; FR-016).
  deliverYourselfHeading: { ...t(30, 'Deliver it yourself'), ...PENDING_CEILING },
  manageBureauCredentials: t(30, 'Manage bureau credentials'),
  historyTransmission: r(30, (a: { bureau: Bureau; result: TransmissionResult; time: string }) =>
    `${bureauName(a.bureau)}: ${RESULT_LABELS[a.result]}, ${a.time}`,
  ),

  // 31. The rejection report (sheet 23/4, 23/5, 23/9; FR-017; squad U17). Hand delivery reuses `deliveredByHand`.
  deliveryChoiceLegend: t(31, 'How was this file delivered?'),
  deliveryChoiceTransmission: r(
    31,
    (a: { time: string; bureau: Bureau; filename: string; result: TransmissionResult }) =>
      `Transmitted from Trellis to ${bureauName(a.bureau)} on ${a.time}: ${a.filename} (${RESULT_LABELS[a.result]})`,
  ),
  prefilledFromTransmission: t(31, 'The bureau is filled in from the transmission you chose.'),

  // 34. The assistant (sheet 22; the throttled variant of 51; the five entry points; FR-008, FR-009,
  // FR-012 to FR-014, FR-018; SAT.1, SAT.2, SAT.5, SAT.7, SAT.9, D.2).
  // Entry points (20/13, 21/7, 12/8, 33/4, 04/11; FR-013).
  assistantOpen: tm(34, 'Ask the assistant'),
  assistantAskAboutRule: tm(34, 'Ask the assistant about this rule'),
  assistantAskAboutColumn: rm(34, (a: { column: string }) => `Ask the assistant about ${a.column}`),
  assistantAskAboutFindings: tm(34, 'Ask the assistant about these findings'),
  assistantCatalogLink: tm(34, 'Ask the assistant about this rule'),
  recentRunLink: tm(34, 'View the most recent run where this rule fired'),
  // The panel (22/1, 22/2, 22/12).
  assistantTitle: tm(34, 'Assistant'),
  assistantClose: tm(34, 'Close the assistant'),
  // Mission 8 review UI4: an empty tenant (its read loading or failed) leaves no dangling part.
  assistantContextLabel: rm(34, (a: { run: string; tenant: string }) =>
    a.tenant === '' ? `Context: run ${a.run}` : `Context: run ${a.run}, ${a.tenant}`,
  ),
  assistantRuleChipRemove: rm(34, (a: { ruleId: string }) => `Remove ${a.ruleId} from the context`),
  assistantPiiLink: tm(34, 'What the assistant receives'),
  // Claim-adjacent (SAT.9, TEN.10, FR-014; S7, U8, R16, analysis A1): it states what leaves Trellis. A question
  // carries the tenant and run ids, the masked text, the rule context and, for a rejected upload, the column name,
  // the expected format and the error count; the last sentence discloses amendment 7's residual.
  assistantPiiStatement: {
    ...tm(
      34,
      "From your file, the assistant receives only an allowlisted projection of this run's findings: the rule ID, the field name, the flagged value where one is available, and the rule's severity, segment and count. For a rejected upload it receives only the column name, the expected format and the error count. Restricted fields such as names, addresses, dates of birth, Social Security numbers, phone numbers and account numbers are never sent from your file. What you type is sent as written, except that Trellis masks long runs of digits and email addresses before sending or storing it. It cannot recognise names, dates of birth, addresses or account numbers that contain letters, so do not type them. Numbers written with dots, slashes or underscores are not masked, so do not type a consumer's identifiers or details.",
    ),
    ...M08_CEILING_REVIEWED,
  },
  // backend/internal/domain/assistant/mask.go MaskToken (FR-007, C-010); provisional-copy.test.ts pins the two.
  assistantMaskToken: tm(34, '[masked]'),
  assistantMaskedNotice: rm(34, (a: { count: Count }) =>
    `Trellis masked ${plural(a.count, 'identifier-shaped value', 'identifier-shaped values')} in this question before sending and storing it.`,
  ),
  assistantEmpty: tm(
    34,
    'Ask about a finding, a rule or a column in this run. When a rule is in context, its static explanation comes first.',
  ),
  // The suggested prompts (22/8). The triage prompt renders nothing at 0, so the composer hides it.
  assistantPromptTriage: rm(34, (a: { count: Count }) =>
    a.count === 0 || a.count === '0' ? '' : `Triage the ${plural(a.count, 'WARN finding', 'WARN findings')} in this run`,
  ),
  assistantPromptDataOrMapping: tm(34, 'Is this a data problem or a mapping problem?'),
  assistantPromptColumn: tm(34, 'My column is called X. Where does it go?'),
  // The prefills an entry point hands the composer (FR-013; OR-9: built here from typed fields, never free text).
  assistantPrefillRule: rm(34, (a: { ruleId: string; count: Count }) =>
    `Why did ${a.ruleId} fire ${a.count === 1 || a.count === '1' ? 'once' : `${n(a.count)} times`} in this run?`,
  ),
  assistantPrefillColumn: rm(34, (a: { column: string; count: Count }) =>
    `Why did ${plural(a.count, 'row', 'rows')} fail validation in column ${a.column}?`,
  ),
  assistantPrefillFindings: rm(34, (a: { count: Count }) =>
    a.count === 1 || a.count === '1'
      ? 'What should I fix about the 1 ERROR finding in this run?'
      : `Which of the ${n(a.count)} ERROR findings in this run should I fix first?`,
  ),
  // The composer and the log (22/3, 22/9; OR-13, U12, U13).
  assistantComposerLabel: tm(34, 'Your question'),
  assistantComposerCount: rm(34, (a: { length: Count; max: Count }) => `${n(a.length)} of ${n(a.max)} characters`),
  assistantSend: tm(34, 'Send'),
  assistantAnswering: tm(34, 'The assistant is answering…'),
  assistantAnswerReceived: tm(34, 'Answer received'),
  assistantYou: tm(34, 'You'),
  // The sources (22/4; FR-001, FR-012, SAT.2).
  assistantSourcesLabel: tm(34, 'Sources'),
  assistantSourceStatic: rm(34, (a: { ruleId: string }) => `Static explanation, ${a.ruleId}`),
  assistantSourceCrrg: rm(34, (a: { ref: string }) => `CRRG ${a.ref}`),
  assistantSourceLayout: rm(34, (a: { column: string }) => `Layout spec, ${a.column}`),
  // The meter and the throttle (22/10, 22/12, 51/10, 51/11; SAT.5, FR-004, FR-005). Dates are format.date strings.
  assistantMeter: rm(34, (a: { used: Count; ceiling: Count }) => `${n(a.used)} of ${n(a.ceiling)} messages this cycle`),
  assistantMeterWarning: rm(34, (a: { used: Count; ceiling: Count; resetsAt: string }) =>
    `${n(a.used)} of ${n(a.ceiling)} messages this cycle. The count resets on ${a.resetsAt}.`,
  ),
  // Claim-adjacent (SAT.5, GL.2): it states the fair-use ceiling and that the product still works at it.
  assistantThrottled: {
    ...rm(34, (a: { ceiling: Count; resetsAt: string }) =>
      `You have used this cycle's ${n(a.ceiling)} assistant messages. The assistant is available again on ${a.resetsAt}. Static explanations and the rule catalog still work.`,
    ),
    ...M08_CEILING_REVIEWED,
  },
  // The refusals (research §1.3; SAT.5, AV.8, GEN.4, D.2).
  assistantBusy: tm(34, 'The assistant is finishing other questions for your organization. Try again in a minute.'),
  assistantUnavailable: tm(
    34,
    'The assistant is unavailable right now. Static explanations in the finding drawer and the rule catalog still work. Try again in a few minutes.',
  ),
  assistantRunNotReady: tm(34, 'The assistant answers once the run is ready or rejected.'),
  assistantRunDataGone: tm(
    34,
    "This run's record-level data was removed at the end of the retention period, so the assistant can no longer answer questions about it.",
  ),
  assistantNoCycle: tm(34, 'The assistant needs an active credit cycle. It becomes available once the subscription is active.'),
  // The conversation (FR-006, FR-016).
  assistantTrimmed: rm(34, (a: { kept: Count }) =>
    `Earlier messages were removed. A conversation keeps its ${n(a.kept)} most recent messages.`,
  ),
  assistantLoadOlder: tm(34, 'Show earlier messages'),
  assistantConversationLabel: tm(34, 'Conversation with the assistant'),
  // Feedback (22/7; FR-011, SAT.8).
  assistantFeedbackUp: tm(34, 'Helpful'),
  assistantFeedbackDown: tm(34, 'Not helpful'),
  assistantFeedbackFailed: tm(34, 'Your rating was not saved. Try again.'),
  // Validation and reads (OR-13).
  assistantQuestionRequired: tm(34, 'Enter a question.'),
  assistantQuestionTooLong: rm(34, (a: { max: Count }) => `A question is at most ${n(a.max)} characters.`),
  assistantReadFailed: tm(34, 'The conversation could not be loaded.'),
  // Mission 8 review UI2 (SAT.5 disclosure): the usage read failed, so the meter cannot show; provisional.
  assistantUsageUnavailable: tm(34, 'Your message count for this cycle could not be loaded.'),
  // Results' retention line names the conversation as Class 1 (22/11; D.2; U10). Download keeps retentionLine.
  retentionLineWithConversation: rm(34, (a: { expiresAt: string }) =>
    `The upload, the files, the record-level findings and the assistant conversation are removed on ${a.expiresAt} under your retention setting.`,
  ),

  // 35. The carried screen fixes (FR-020 to FR-023; mission 6 A-L5, mission 7 follow-ups; analysis I11).
  creditsUnavailable: tm(35, 'Credits unavailable'),
  severityCountsUnavailable: tm(35, 'The severity counts could not be loaded.'),
  rowErrorsUnavailable: tm(35, 'The row-level errors could not be read.'),
  trailerNoStatistics: tm(35, 'The trailer declares no statistics.'),
  zeroListed: tm(35, 'None listed'),
  transmissionsReadFailed: tm(35, "Trellis could not read this run's transmissions, so the report cannot be filed yet."),

  // 36. Billing (mission 9 WP11 T068; sheets 06, 08, 11, 50, 51; B.1 to B.7, T.5, NF.9; FR-019, C-009, C-010;
  // U25: money is a money() string, a date a format.date string, a count a number).
  // Sheet 08, the subscribe page (08/1 to 08/11; U23: the stepper order).
  subscribeTitle: t(36, 'Choose your plan'),
  stepperAccount: t(36, 'Create account'),
  stepperSecure: t(36, 'Two-step sign-in'),
  stepperVerify: t(36, 'Verify'),
  stepperSubscribe: t(36, 'Subscribe'),
  planMonthly: r(36, (a: { price: string; interval: 'month' | 'year' }) => `Monthly, ${a.price} per ${a.interval} plus applicable tax`),
  planAnnual: r(36, (a: { price: string; interval: 'month' | 'year' }) => `Annual, ${a.price} per ${a.interval} plus applicable tax`),
  planAnnualAllowance: r(36, (a: { allowance: Count; yearTotal: Count }) =>
    `The annual plan includes ${n(a.allowance)} credits in each monthly cycle, not ${n(a.yearTotal)} up front.`,
  ),
  planTermsAllowance: r(36, (a: { allowance: Count }) =>
    `${n(a.allowance)} credits in each monthly billing cycle. Unused credits do not roll over.`,
  ),
  planTermsAssistant: r(36, (a: { ceiling: Count }) => `The assistant answers up to ${n(a.ceiling)} messages in each cycle.`),
  // Claim-adjacent (T.3, TX.8): it states what transmission costs.
  planTermsTransmission: { ...t(36, 'Transmitting a file to a bureau uses no credits.'), ...PENDING_CEILING },
  planTermsNoSeatFee: t(36, 'There is no per-user fee.'),
  planTermsOverage: r(36, (a: { price: string; unit: Count; months: Count }) =>
    `Extra credits are sold in blocks, from ${a.price} per ${n(a.unit)} credits. Purchased credits expire ${n(a.months)} months after purchase and are used after your included credits.`,
  ),
  softCapStatement: r(36, (a: { ceiling: Count }) =>
    `In your first billing cycle only, runs can go past your included credits with a warning, and up to ${n(a.ceiling)} extra credits are forgiven. From your second cycle, a run that needs more credits than you have is blocked before it starts.`,
  ),
  // B.3, GL.4; OR-13: what a credit meters. One key, one component (MeteringStatement). Counsel B6.
  meteringStatement: {
    ...t(
      36,
      'Trellis uses one credit per record generated and one credit per record analyzed from an uploaded Metro 2® file. Re-analyzing files generated by Trellis, layout validation, structural checks, failed or cancelled runs, and bureau transmissions consume no credits.',
    ),
    ...counsel('B6'),
  },
  // Counsel B6: one sentence for both plans; `annual` stays in the signature for the callers.
  cancelTermsAck: {
    ...r(
      36,
      (_a: { annual: boolean }) =>
        'I understand that I may cancel my subscription at any time, that access continues through the end of the current paid billing period, and that all payments are non-refundable, including annual plan payments.',
    ),
    ...counsel('B6'),
  },
  billingEmailLabel: t(36, 'Billing email'),
  billingEmailHint: t(36, 'Receipts, invoices and payment notices go here.'),
  subscribeAction: t(36, 'Subscribe'),
  subscribeDisabledReason: t(36, 'Tick the cancellation terms to continue.'),
  // SC-36632 (D.6, FR-013): the way to the privacy controls before a subscription.
  subscribeDeleteNote: t(36, 'You can delete your account and its data at any time, also before you subscribe.'),
  subscribeDeleteLink: t(36, 'Data and account deletion'),
  checkoutPendingTitle: t(36, 'Confirming your subscription'),
  checkoutPendingBody: t(36, 'This usually takes a few seconds.'),
  checkoutSlowTitle: t(36, 'Still confirming'),
  checkoutSlowBody: t(36, 'Trellis has not received the confirmation yet. Check again in a minute.'),
  checkoutRetry: t(36, 'Check again'),
  checkoutCancelled: t(36, 'Checkout was not completed. Nothing was charged.'),
  // Sheet 50, billing settings (50/1 to 50/13).
  billingTitle: t(36, 'Billing'),
  planCardTitle: t(36, 'Plan'),
  planRenews: r(36, (a: { date: string }) => `Renews on ${a.date}`),
  planEnds: r(36, (a: { date: string }) => `Access continues until ${a.date}. Your plan will not renew.`),
  planCancel: t(36, 'Cancel plan'),
  planCancelConfirmTitle: t(36, 'Cancel your plan?'),
  planCancelKeep: t(36, 'Keep plan'),
  planUndo: t(36, 'Keep my plan'),
  planCancelConfirmBody: r(36, (a: { date: string; annual: boolean }) =>
    `Access continues until ${a.date}, and there is no refund.${a.annual ? ' There is no pro-rata refund on the annual plan.' : ''} Runs still queued on that date are cancelled and use no credits.`,
  ),
  planSwitchTo: r(36, (a: { plan: string; price: string; interval: 'month' | 'year' }) =>
    `Switch to ${a.plan} (${a.price} per ${a.interval})`,
  ),
  planSwitchPending: r(36, (a: { plan: string; date: string }) => `Switches to ${a.plan} on ${a.date}.`),
  planSwitchNote: t(36, 'The switch takes effect at your next renewal. Your credits per cycle do not change.'),
  softCapActive: r(36, (a: { date: string; forgiven: Count; ceiling: Count }) =>
    `First-cycle soft cap: active until ${a.date}. ${n(a.forgiven)} of ${n(a.ceiling)} extra credits forgiven so far.`,
  ),
  softCapClosed: r(36, (a: { forgiven: Count }) => `First-cycle soft cap: closed. ${n(a.forgiven)} extra credits were forgiven.`),
  creditsPanelTitle: t(36, 'Credits'),
  creditsIncluded: t(36, 'Included this cycle'),
  creditsPurchased: t(36, 'Purchased'),
  creditsAvailable: t(36, 'Available'),
  creditsIncludedNote: t(
    36,
    'Included credits reset each cycle and do not roll over. Purchased credits are used after them, the block expiring first.',
  ),
  blocksTitle: t(36, 'Purchased blocks'),
  blocksColCredits: t(36, 'Credits'),
  blocksColRemaining: t(36, 'Remaining'),
  blocksColPurchased: t(36, 'Purchased'),
  blocksColExpires: t(36, 'Expires'),
  blocksEmpty: t(36, 'No purchased credits.'),
  blockExpiring: r(36, (a: { days: Count }) => `Expires in ${plural(a.days, 'day', 'days')}`),
  buyTitle: t(36, 'Buy credits'),
  buyAction: t(36, 'Buy'),
  buyNoCard: t(36, 'Add a card before buying credits.'),
  buyTier: r(36, (a: { credits: Count; price: string; per: string; unit: Count }) =>
    `${n(a.credits)} credits, ${a.price} (${a.per} per ${n(a.unit)})`,
  ),
  buyConfirmBody: r(36, (a: { price: string; card: string; credits: Count }) =>
    `Charge ${a.price} to ${a.card} for ${n(a.credits)} credits?`,
  ),
  buyDone: r(36, (a: { credits: Count }) => `${n(a.credits)} credits added.`),
  cardTitle: t(36, 'Card on file'),
  cardNone: t(36, 'No card on file.'),
  cardUpdate: t(36, 'Update card'),
  cardValue: r(36, (a: { brand: string; last4: string }) => `${a.brand} ending ${a.last4}`),
  // Claim-adjacent (T.6; spec amendments 9 and 11): what reaches Trellis of a card. Counsel B6; it names
  // Stripe and PCI-DSS, so Compliance still reads it.
  cardProviderNote: {
    ...t(
      36,
      'Payment card details are submitted directly to our PCI-DSS compliant payment processor (Stripe) and are never received, stored, or processed by Trellis servers.',
    ),
    ...PENDING_CEILING_COMPLIANCE,
    ...counsel('B6'),
  },
  ledgerTitle: t(36, 'Usage ledger'),
  ledgerColDate: t(36, 'Date'),
  ledgerColMovement: t(36, 'Movement'),
  ledgerColCredits: t(36, 'Credits'),
  ledgerColBalance: t(36, 'Balance after'),
  ledgerOlder: t(36, 'Show older entries'),
  ledgerGrant: t(36, 'Cycle allowance'),
  ledgerPurchase: t(36, 'Purchased block'),
  ledgerForgiveness: t(36, 'First-cycle forgiveness'),
  ledgerExpiryIncluded: t(36, 'Unused allowance expired'),
  ledgerExpiryBlock: t(36, 'Purchased block expired'),
  ledgerFreeUpload: t(36, 'Upload rejected by validation'),
  ledgerFree: t(36, 'Free'),
  // A suffix of a run's movement ("Run 3f0c9d2e completed · analysis free"), so it starts in lower case.
  ledgerAnalysisFree: t(36, 'analysis free'),
  ledgerRun: r(36, (a: { run: string }) => `Run ${a.run}`),
  ledgerRunReturned: r(36, (a: { run: string }) => `Run ${a.run} returned`),
  ledgerRunCompleted: r(36, (a: { run: string }) => `Run ${a.run} completed`),
  ledgerFreeTransmission: r(36, (a: { run: string }) => `Transmission for run ${a.run}`),
  ledgerAllocation: r(36, (a: { included: Count; purchased: Count }) => `${n(a.included)} included, ${n(a.purchased)} purchased`),
  documentsTitle: t(36, 'Invoices and statements'),
  invoiceOpen: t(36, 'View invoice'),
  statementDownload: t(36, 'Download CSV'),
  statementDownloadFailed: t(36, 'The statement could not be downloaded. Try again.'),
  invoiceRow: r(36, (a: { period: string; amount: string; status: string }) => `${a.period}, ${a.amount}, ${a.status}`),
  statementRow: r(36, (a: { date: string }) => `Usage statement for the cycle starting ${a.date}`),
  providerUnavailable: t(36, 'Card and invoice details could not be loaded. Your credits above are current.'),
  // Sheet 51, the shell and the refused surfaces (51/4 to 51/9; U29: "still in retention").
  dunningBanner: r(36, (a: { days: Count; date: string }) =>
    `Your last payment failed. Update your card within ${plural(a.days, 'day', 'days')} to keep full access. From ${a.date}, your account is read-only.`,
  ),
  readOnlyBannerDunning: t(
    36,
    'Your account is read-only because a payment failed. Your runs and files still in retention stay available to view and download. Update your card to restore access.',
  ),
  readOnlyBannerCancelled: r(36, (a: { date: string }) =>
    `Your plan ended on ${a.date}, so your account is read-only. Your runs and files still in retention stay available to view and download. Subscribe to start new runs.`,
  ),
  readOnlyRemedyUpdate: t(36, 'Update card'),
  readOnlyRemedySubscribe: t(36, 'Subscribe'),
  undoCancelRemedy: t(36, 'Keep my plan'),
  disabledReadOnlyDunning: t(36, 'Read-only until the payment is fixed.'),
  disabledReadOnlyCancelled: t(36, 'Read-only: your plan has ended.'),
  disabledPending: t(36, 'Subscribe to use this.'),
  gatedReadOnly: t(36, 'Read-only'),
  chipIncluded: r(36, (a: { n: Count }) => `${n(a.n)} included`),
  chipDunning: t(36, 'Payment failed'),
  planLabelMonthly: t(36, 'Monthly plan'),
  planLabelAnnual: t(36, 'Annual plan'),
  planLabelEnding: r(36, (a: { date: string }) => `Ends ${a.date}`),
  softCapWarning: r(36, (a: { overage: Count; ceiling: Count; date: string }) =>
    `This run goes ${n(a.overage)} credits past your balance. In your first cycle, up to ${n(a.ceiling)} extra credits are forgiven. From ${a.date}, a run like this is blocked before it starts.`,
  ),
  softCapAccounting: r(36, (a: { forgiven: Count; ceiling: Count }) => `Forgiven this cycle: ${n(a.forgiven)} of ${n(a.ceiling)}.`),
  blockedBuyCredits: t(36, 'Buy credits'),
  // True since OR-12 (the blocked estimate's email); the copy owner reviews it.
  blockedEmailNote: t(36, 'A copy of this notice was sent to your billing email.'),
  transmissionEmailNote: t(36, 'The result is also sent to your billing email.'),
  // The problem rows (research §1.18; U21): billing problems render these, never `detail` (U25).
  problemReadOnlyTitle: t(36, 'Read-only'),
  problemReadOnlyDunning: t(36, 'This needs full access. A payment failed, so the account is read-only.'),
  problemReadOnlyCancelled: t(36, 'This needs full access. Your plan has ended, so the account is read-only.'),
  problemReadOnlyPending: t(36, 'This needs an active plan.'),
  problemBillingConflictTitle: t(36, 'This cannot be done now'),
  'problemState.active': t(36, 'You already have a plan.'),
  'problemState.cancelling': t(36, 'Your plan is set to cancel. Keep it instead of subscribing again.'),
  'problemState.dunning': t(36, 'Fix the failed payment first.'),
  'problemState.no-subscription': t(36, 'Subscribe first.'),
  'problemState.not-cancelling': t(36, 'Your plan is not set to cancel.'),
  'problemState.access-ended': t(36, 'Your plan has ended. Subscribe to continue.'),
  'problemState.same-plan': t(36, 'You are already on that plan.'),
  'problemState.request-reused': t(36, 'This purchase was already started with a different size. Start a new purchase.'),
  'problemState.ending': t(36, 'Your previous plan is still closing. Try again in a few minutes.'),
  problemPaymentFailedTitle: t(36, 'Payment failed'),
  problemPaymentFailed: t(36, 'The card was declined. Nothing was charged.'),
  // The fourteen email keys (TR-10, P2; research §1.23): the provider's templates for mission 12, each
  // placeholder a data key of its email-data-keys.json template. "We" is the product speaking.
  emailReceiptSubject: t(36, 'Your Trellis receipt'),
  emailReceiptBody: t(36, 'Payment of {amount_usd} for {period_start} to {period_end} received. View the invoice: {invoice_url}'),
  emailDunningSubject: t(36, 'Your Trellis payment failed'),
  emailDunningBody: t(36, 'We could not take your payment on {failed_at}. Update your card by {read_only_at} to keep full access: {fix_url}'),
  emailReadOnlySubject: t(36, 'Your Trellis account is read-only'),
  emailReadOnlyBody: t(
    36,
    'Since {read_only_at} your account is read-only. Your runs and files still in retention stay available to view and download. {remedy_url}',
  ),
  emailSoftCapSubject: t(36, 'A run went past your included credits'),
  emailSoftCapBody: t(
    36,
    'In your first cycle, up to {ceiling} extra credits are forgiven; {forgiven} so far. From {cycle_end}, runs over your balance are blocked.',
  ),
  emailRunBlockedSubject: t(36, 'A run was blocked'),
  emailRunBlockedBody: t(36, 'A run needed {required} credits and your balance was {balance}. Buy credits: {buy_url}'),
  emailCreditsExpiringSubject: t(36, 'Purchased credits expire soon'),
  emailCreditsExpiringBody: t(36, '{credits_remaining} purchased credits expire on {expires_at}. {billing_url}'),
  emailTransmissionResultSubject: t(36, 'Transmission result for run {run_short_id}'),
  emailTransmissionResultBody: t(36, 'The transmission to {bureau} finished: {result}. {log_url}'),

  // 37. Retention and deletion (mission 10 WP15 T086; sheets 43, 44, 10, 22, 30, 31; D.2, D.3, D.6, D.8, A.5,
  // UC-04; FR-017 to FR-019, FR-022, C-007, C-008, C-013). Every period is an argument from `terms`.
  // Sheet 43, the retention control (43/2, 43/3).
  retentionLabel: t(37, 'Class 1 retention, in days'),
  retentionHelp: {
    ...r(37, (a: { days: Count }) =>
      `Controls how many days each run's Class 1 Consumer Data is retained after completion. The default is ${plural(a.days, 'day', 'days')}. Class 2 Account Records (audit logs, run counts, billing records) are retained separately for compliance.`,
    ),
    ...counsel('C2'),
  },
  retentionBounds: r(37, (a: { min: Count; max: Count }) => `Enter a whole number of days from ${n(a.min)} to ${n(a.max)}.`),
  retentionLastChanged: r(37, (a: { date: string }) => `Last changed on ${a.date}`),
  retentionSave: t(37, 'Save'),
  retentionDiscard: t(37, 'Discard'),
  retentionUnsaved: t(37, 'The retention window has an unsaved change.'),
  // The shortening dialog (43/3): the exact count, at most the listed runs, and a note when truncated (amendment 26).
  shortenTitle: t(37, 'Shorten the retention window?'),
  // Counsel C2: `days` is the new window the change asks for.
  shortenCount: {
    ...r(37, (a: { days: Count; count: Count; minutes: Count }) =>
      `Warning: Changing your retention window to ${plural(a.days, 'day', 'days')} will make ${plural(a.count, 'existing run', 'existing runs')} eligible for immediate deletion. Their Class 1 Consumer Data will be permanently purged within ${plural(a.minutes, 'minute', 'minutes')} of saving. This action cannot be undone.`,
    ),
    ...counsel('C2'),
  },
  'shortenRunHeadings.run': t(37, 'Run'),
  'shortenRunHeadings.completed': t(37, 'Completed'),
  'shortenRunHeadings.currentDue': t(37, 'Deletion date now'),
  'shortenRunHeadings.newDue': t(37, 'New deletion date'),
  shortenTruncated: r(37, (a: { more: Count }) => `And ${n(a.more)} more. The runs table on this page lists them all.`),
  shortenConfirm: t(37, 'Shorten and save'),
  // Retention 0 (43/4, 30/11, 31; UC-04). "Earlier", never "immediately": the first sweep after the trigger
  // (Contradictions 6). WP15 cycle 2 (orchestrator; research §1.3, amendment 6): no overclaim, so the sentence
  // states the wait while a first transmission is pending and the lag of up to one sweep interval
  // (`minutes` is `terms.sweep_interval_minutes`). The gated labels are the demoted buttons' (U17).
  // Counsel C3, conformed: "earlier", never "immediately" (Contradictions 6); the trigger is the first transmit
  // request settling, whatever its result, or a download of the generated file or the upload (EffectiveDue), not
  // a successful delivery; "assistant", never "AI" (U9);
  // and the second-bureau note says "in the same transmission", the product's unit, not "session".
  retentionZeroWarning: {
    ...r(37, (a: { hours: Count; minutes: Count }) =>
      `With retention set to zero days, each run's Class 1 Consumer Data is deleted ${plural(a.hours, 'hour', 'hours')} after completion, or earlier once the generated file or the original upload is downloaded or its first bureau transmission settles. While a bureau transmission is pending, deletion waits until the transmission status settles. Deletion executes within ${plural(a.minutes, 'minute', 'minutes')} of becoming due. Once deleted, validation findings, detailed error reports, and assistant conversation histories for that run are permanently removed and cannot be recovered.`,
    ),
    ...counsel('C3'),
  },
  retentionZeroAck: {
    ...t(
      37,
      'I understand that under zero retention, Class 1 Consumer Data, validation findings, and assistant logs are permanently deleted shortly after processing and cannot be recovered.',
    ),
    ...counsel('C3'),
  },
  retentionZeroSaveGated: t(37, 'Save (acknowledge first)'),
  retentionZeroDownloadGated: t(37, 'Download (acknowledge first)'),
  retentionZeroSecondBureau: {
    ...t(
      37,
      'Note: If transmitting to multiple credit bureaus, initiate delivery to all intended bureaus in the same transmission. Once delivery settles under zero retention, the generated Metro 2® file is purged and cannot be re-sent without creating a new run.',
    ),
    ...counsel('C3'),
  },
  // The digest's own rule (NF.9; OR-15, U19): no email below `digest_min_window_days`, the note at or above it.
  // Counsel C2 states the longest window without an email: `days` is `digest_min_window_days` less one.
  retentionNoDigestNote: {
    ...r(37, (a: { days: Count }) =>
      `Expiration notice emails are not sent for retention windows set to ${plural(a.days, 'day', 'days')} or shorter.`,
    ),
    ...counsel('C2'),
  },
  retentionExpiryEmailNote: {
    ...r(37, (a: { days: Count }) =>
      `You will receive an automated email notification ${plural(a.days, 'day', 'days')} before a run's Class 1 Data is deleted.`,
    ),
    ...counsel('C2'),
  },
  // The cards (43/5 to 43/7, 44/14). D.2's objects in plain language; AgentCore memory is not claimed
  // (Contradictions 5). The encryption statement names stored files only (S13; amendment 3).
  class1Title: t(37, 'Class 1: consumer data'),
  // Counsel C4, conformed: "assistant", never "AI" (U9).
  class1List: {
    ...t(
      37,
      "Class 1 Consumer Data includes uploaded files, generated Metro 2® files, validation reports, record-level findings, assistant conversations, and bureau rejection text; Trellis permanently purges all Class 1 Data upon the expiration of your organization's configured retention window.",
    ),
    ...counsel('C4'),
  },
  class2Title: t(37, 'Class 2: account records'),
  // Counsel C4 (Q14, Q15), the period from `terms` (C-013).
  class2Statement: {
    ...r(37, (a: { years: Count }) =>
      `Class 2 Account Records contain no consumer identifiers and consist of run counts, audit logs, bureau transmission logs, and billing history; they are retained while your account remains active and for ${plural(a.years, 'year', 'years')} following account closure.`,
    ),
    ...counsel('C4'),
  },
  encryptionTitle: t(37, 'Encryption and isolation'),
  encryptionStatement: {
    ...t(
      37,
      "Your stored files are encrypted under a key unique to your organization, and your organization's data is kept apart from every other organization's at the data layer.",
    ),
    ...KMS_GATE,
  },
  // The runs table (43/8; U26: a null due_at waits on the first transmission).
  runsTableTitle: t(37, 'Runs holding Class 1 data'),
  'runsTableHeadings.run': t(37, 'Run'),
  'runsTableHeadings.completed': t(37, 'Completed'),
  'runsTableHeadings.due': t(37, 'Deletion date'),
  'runsTableHeadings.holds': t(37, 'Still holds'),
  holdsUpload: t(37, 'Upload'),
  holdsGenerated: t(37, 'Generated file'),
  holdsFindings: t(37, 'Findings'),
  holdsConversation: t(37, 'Assistant conversation'),
  holdsRejectionText: t(37, 'Rejection text'),
  holdsValidationReport: t(37, 'Validation report'),
  runsWaitingForTransmission: t(37, 'Waiting for the transmission to settle'),
  // Mission 10 WP16 cycle 2 (orchestrator ruling; review finding 4; style guide §7.14): the runs table's empty state.
  runsTableEmpty: t(37, 'No run holds Class 1 data right now.'),
  retentionTimezoneNote: t(37, 'Dates are in UTC.'),
  // Export (43/9; D.6): fulfilled by the runbook within `fulfilment_days`.
  exportTitle: t(37, 'Data export'),
  exportBody: {
    ...t(
      37,
      "Request an encrypted export bundle containing all active Class 1 Consumer Data within your retention window and your organization's complete Class 2 Account Records.",
    ),
    ...counsel('C5'),
  },
  exportButton: t(37, 'Request export'),
  exportFulfilment: {
    ...r(37, (a: { days: Count }) => `Bloom Credit fulfills all data export requests within ${plural(a.days, 'calendar day', 'calendar days')}.`),
    ...counsel('C5'),
  },
  exportOpen: r(37, (a: { date: string }) => `Export requested. It is due by ${a.date}.`),
  // Deletion (43/10, 43/11; D.6, D.8): the contract in plain language.
  deletionTitle: t(37, 'Delete account'),
  deletionBody: {
    ...t(37, "Permanently close your organization's account and delete all consumer reporting data. This action is irreversible."),
    ...counsel('C6'),
  },
  // Counsel C6, conformed: "now" for "immediately" (register 37), the storage encryption key (AV.6 as amended),
  // the periods from `terms` (C-013).
  deletionDialogContract: {
    ...r(37, (a: { days: Count; years: Count }) =>
      `Upon confirmation, your account will be closed now and pending runs cancelled without charge. Within ${plural(a.days, 'day', 'days')}, all Class 1 Consumer Data will be permanently purged and your organization's storage encryption key destroyed. Class 2 Account Records are retained for ${plural(a.years, 'year', 'years')} as required for audit and legal compliance. Active subscriptions terminate now without refund. Any open data export request will be fulfilled prior to final system deletion.`,
    ),
    ...counsel('C6'),
  },
  deletionTypedPrompt: t(37, "Type your organization's legal name to confirm"),
  deletionTypedHelper: t(37, 'It must match exactly, including capitals and punctuation. Delete account stays unavailable until it does.'),
  deletionPasswordPrompt: t(37, 'Password'),
  deletionNameMismatch: t(37, "That is not your organization's legal name as Trellis holds it. Nothing was changed."),
  deletionPasswordFailed: t(37, 'The password was not accepted. Nothing was changed.'),
  // Mission 10 WP16 cycle 2 (orchestrator ruling; review finding 1; FR-024, style guide §9): Delete's stated reason
  // while the password is empty.
  deletionPasswordRequired: t(37, 'Enter your password. Delete account stays unavailable until you do.'),
  deletionExportFirst: { ...t(37, 'Your open export request is fulfilled before the deletion runs.'), ...Q14 },
  deletionSignedOutNotice: t(
    37,
    'Your account deletion is requested and you have been signed out. A confirmation email follows when the deletion is complete.',
  ),
  // The audit log (sheet 44; NFR §4.2 A.5: the log attributes to the organization, not a person).
  auditAttribution: t(
    37,
    'Events are attributed to your organization. Trellis has one sign-in per organization, so the log cannot show which person took an action.',
  ),
  auditTimezone: t(37, 'Times are in UTC. Hover over or focus a time to see your local time.'),
  auditFilterType: t(37, 'Event type'),
  auditFilterFrom: t(37, 'From'),
  auditFilterTo: t(37, 'To'),
  auditFilterText: t(37, 'Search target and details'),
  auditEmpty: t(37, 'No events match these filters.'),
  auditExportCsv: t(37, 'Export CSV'),
  auditExportFailed: t(37, 'The audit log could not be exported. Try again.'),
  'auditTableHeadings.time': t(37, 'Time (UTC)'),
  'auditTableHeadings.type': t(37, 'Event'),
  'auditTableHeadings.target': t(37, 'Target'),
  'auditTableHeadings.details': t(37, 'Details'),
  'auditTableHeadings.ip': t(37, 'IP address'),
  'auditTableHeadings.userAgent': t(37, 'User agent'),
  // The labels of the fields the writers record (OR-15, U18): no size, remote filename, rule list or last 4.
  'auditDetail.bureau': t(37, 'Bureau'),
  'auditDetail.bytes': t(37, 'Bytes'),
  'auditDetail.result': t(37, 'Result'),
  'auditDetail.file': t(37, 'File'),
  'auditDetail.checksum': t(37, 'Checksum'),
  'auditDetail.retention': r(37, (a: { from: Count; to: Count }) => `${plural(a.from, 'day', 'days')} to ${plural(a.to, 'day', 'days')}`),
  'auditDetail.version': t(37, 'Version'),
  'auditDetail.fieldsChanged': t(37, 'Fields changed'),
  'auditDetail.method': t(37, 'Method'),
  'auditDetail.request': t(37, 'Request'),
  'auditDetail.dueOn': t(37, 'Due on'),
  'auditDetail.queuedRunsCancelled': t(37, 'Queued runs cancelled'),
  'auditDetail.errorCount': r(37, (a: { count: Count }) => `${plural(a.count, 'ERROR finding', 'ERROR findings')} acknowledged`),
  // Mission 10 WP17 (T101; orchestrator ruling: a spine key on lane-q, WP15 approved; OR-15, U18): the labels of
  // the other recorded fields sheet 44 renders, one per field a writer records.
  'auditDetail.run': t(37, 'Run'),
  'auditDetail.reason': t(37, 'Reason'),
  'auditDetail.actor': t(37, 'By'),
  'auditDetail.plan': t(37, 'Plan'),
  'auditDetail.credits': t(37, 'Credits'),
  'auditDetail.failedAt': t(37, 'Failed at'),
  'auditDetail.from': t(37, 'From'),
  'auditDetail.to': t(37, 'To'),
  'auditDetail.typeCount': t(37, 'Event types'),
  'auditDetail.files': t(37, 'Files'),
  'auditDetail.reopenedFrom': t(37, 'Reopened from'),
  'auditDetail.keyDestroyOn': t(37, 'Storage key destroyed on'),
  // One label per type in audit.WrittenTypes() (U28), the eleven billing and five lifecycle types included.
  'auditType.login': t(37, 'Sign-in'),
  'auditType.login_failed': t(37, 'Failed sign-in'),
  'auditType.logout': t(37, 'Sign-out'),
  'auditType.download': t(37, 'Download'),
  'auditType.transmission': t(37, 'Transmission'),
  'auditType.error_acknowledgment': t(37, 'ERROR acknowledgment'),
  'auditType.credential_changed': t(37, 'Credentials changed'),
  'auditType.credential_test_started': t(37, 'Connection test started'),
  'auditType.credential_tested': t(37, 'Connection tested'),
  'auditType.header_param_changed': t(37, 'Header parameter changed'),
  'auditType.terms_accepted': t(37, 'Terms accepted'),
  'auditType.tenant_verified': t(37, 'Organization verified'),
  'auditType.mfa_enrolled': t(37, 'Two-step sign-in set up'),
  // The sign-in backup codes, not a data backup: the C-008 pattern matches the word, so the copy owner reads it.
  'auditType.backup_codes_regenerated': { ...t(37, 'Backup codes regenerated'), ...PENDING_CEILING },
  'auditType.password_reset': t(37, 'Password reset'),
  'auditType.billing_checkout_started': t(37, 'Checkout started'),
  'auditType.billing_subscribed': t(37, 'Subscribed'),
  'auditType.billing_cancel_scheduled': t(37, 'Cancellation scheduled'),
  'auditType.billing_cancel_undone': t(37, 'Cancellation undone'),
  'auditType.billing_plan_switch_scheduled': t(37, 'Plan switch scheduled'),
  'auditType.billing_card_update_started': t(37, 'Card update started'),
  'auditType.credits_purchased': t(37, 'Credits purchased'),
  'auditType.billing_dunning_started': t(37, 'Payment failed'),
  'auditType.billing_read_only_started': t(37, 'Read-only started'),
  'auditType.billing_read_only_ended': t(37, 'Read-only ended'),
  'auditType.billing_email_changed': t(37, 'Billing email changed'),
  'auditType.retention_changed': t(37, 'Retention changed'),
  'auditType.data_export_request': t(37, 'Data export request'),
  'auditType.deletion_request': t(37, 'Deletion request'),
  'auditType.data_export_completed': t(37, 'Data export completed'),
  'auditType.deletion_completed': t(37, 'Deletion completed'),
  // Sheet 10 (10/9) and sheet 22 (22/11; amendment 21: the Trellis conversation, never AgentCore memory).
  findingsExpired: t(37, 'Findings expired'),
  conversationRemoved: t(
    37,
    "This run's assistant conversation was removed when its Class 1 data was deleted under your retention setting.",
  ),
  // The D.3 problem row (research §1.22): the upload grant's 503 while the tenant key is not ready.
  problemTenantKeyMissing: t(37, "Your organization's storage is not ready yet. Try again in a few minutes."),
  // The four email keys (research §1.23): the provider's templates, each placeholder a data key of its
  // email-data-keys.json template; the deletion email has no URL (there is no sign-in).
  // Counsel C7, conformed to the data keys (C-013): "in 7 days" is "on {expires_on}", "seven (7) years" is
  // "{class2_years} years", and the key's "7-day" window, which no key carries, is dropped.
  emailRetentionExpiringSubject: {
    ...t(37, 'Trellis Alert: {run_count} reporting runs scheduled for data deletion on {expires_on}'),
    ...counsel('C7'),
  },
  emailRetentionExpiringBody: {
    ...t(
      37,
      "{run_count} reporting runs will reach the end of your organization's retention window on {expires_on}. At that time, all associated Class 1 Consumer Data will be permanently deleted.\nAffected Runs: {run_short_ids} (and {more_count} others).\nIf you need to retain this data, please export it or adjust your retention settings prior to {expires_on}: {settings_url}",
    ),
    ...counsel('C7'),
  },
  emailDeletionCompleteSubject: {
    ...t(37, 'Trellis Confirmation: Account deletion request completed (Ref: {request_short_id})'),
    ...counsel('C7'),
  },
  emailDeletionCompleteBody: {
    ...t(
      37,
      "Account deletion request {request_short_id} completed on {completed_at}. All Class 1 Consumer Data has been permanently purged from active systems. Your organization's storage encryption key is scheduled for final destruction on {key_destroy_on} following the mandatory security window. Class 2 Account Records will be retained for {class2_years} years in accordance with our terms of service.",
    ),
    ...counsel('C7'),
  },
} satisfies Record<string, CopyEntry>

export type CopyKey = keyof typeof PROVISIONAL_COPY

/** The keys of `text` entries. */
export type TextKey = { [K in CopyKey]: (typeof PROVISIONAL_COPY)[K] extends { text: string } ? K : never }[CopyKey]

/**
 * `freeItems` as style guide §8's list (owner decision 2026-09-26, decision 17): the lead and its four items,
 * cut from the register's sentence so the wording stays in one place.
 */
export function freeItemsList(): { lead: string; items: string[] } {
  const sentence = copyText('freeItems')
  const colon = sentence.indexOf(': ')
  const items = sentence
    .slice(colon + 2)
    .replace(/\.$/, '')
    .split(', ')
    .map((item) => item.replace(/^and /, ''))
  return { lead: sentence.slice(0, colon + 1), items }
}

/** A `text` entry's sentence. */
export function copyText(key: TextKey): string {
  const entry: CopyEntry = PROVISIONAL_COPY[key]
  if (!('text' in entry)) throw new Error(`provisional-copy: ${key} is a render entry`)
  return entry.text
}
